import { STEP } from "#src/constants/step.ts";
import { PRODUCT_TYPE } from "#src/constants/products.ts";

import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import { integerToPercent } from "#src/util/helpers.ts";

import { useInstanceSettingsStore } from "#src/stores/instance-settings.ts";
import {
  getPartyStore,
  useEappStore,
} from "#src/stores/electronic-application.ts";
import {
  usePrimaryInsuredStore,
  useJointInsuredStore,
} from "#src/stores/insured.ts";
import { useQuotingStore } from "#src/stores/quoting.ts";
import {
  type CaseContractParty,
  type CaseContractPartyRole,
  useCaseStore,
} from "#src/stores/case.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed, type Ref, ref } from "vue";
import { type BaseStepStore } from "#src/types/StepStore.ts";
import { type Mode } from "#src/constants/mode.ts";
import { type RoleType } from "#src/types/Role.ts";
import { type SavableProperty } from "#src/composables/savable-property.composable.ts";

interface StoredContractParty {
  roles: Record<RoleType, number>;
  beneficiary_amount: SavableProperty;
}

interface CompletedState {
  showSigningByEmail: boolean;
  showCompletedContext: boolean;
  caseId: number | string | null;
  caseUuid: string | null;
  resumeCarrierEapp: boolean | null;
  resumeCarrierEappText: string | null;
  premium: number | null;
  amount: number | null;
  status: string | null;
  mode: Mode | null;
  line: string | null;
  policyNumber: string | null;
  state: string | null;
  sharableLink: string | null;
  inforceDate: string | null;
  appliedRating: string | null;
  approvedRating: string | null;
  appliedPremium: number | null;
  approvedPremium: number | null;
  carrier: {
    avatarUrl: string | null;
    name: string | null;
  };
  product: {
    name: string | null;
  };
  primaryAgent: {
    name: string | null;
    email: string | null;
    phone: string | null;
    avatarUrl: string | null;
    type: string | null;
    schedulingLink: string | null;
  };
  requirements: string[];
  contractParties: {
    name: string;
    roles: ContractPartyRole[];
  }[];
}

interface ContractPartyRole {
  role: RoleType;
  beneficiaryAmount?: number;
}

interface CompletedStep extends BaseStepStore, ToSetupState<CompletedState> {
  setContext(): void;
  useCaseContext(): void;
  useEappContext(): void;
}

function initialState(): CompletedState {
  return {
    showSigningByEmail: false,
    showCompletedContext: false,
    caseId: null,
    caseUuid: null,
    resumeCarrierEapp: null,
    resumeCarrierEappText: null,
    premium: null,
    amount: null,
    status: null,
    mode: null,
    line: null,
    policyNumber: "Pending",
    state: null,
    sharableLink: null,
    inforceDate: null,
    appliedRating: null,
    approvedRating: null,
    appliedPremium: null,
    approvedPremium: null,
    carrier: {
      avatarUrl: null,
      name: null,
    },
    product: {
      name: null,
    },
    primaryAgent: {
      name: null,
      email: null,
      phone: null,
      avatarUrl: null,
      type: null,
      schedulingLink: null,
    },
    requirements: [],
    contractParties: [],
  };
}

function initializeCompletedStep(pinia: Pinia): CompletedStep {
  const state = generateInitialState<CompletedState>(initialState());

  function $reset() {
    resetState<CompletedState>(state, initialState());
  }

  const step = computed(() => STEP.COMPLETED);
  const next = computed(() => null);
  const previous = computed(() => null);
  const hidesFooter = computed(() => true);
  const hidesHeader = computed(() => true);
  const hidesShare = computed(() => true);
  const skipStepSave = computed(() => {
    const caseStore = useCaseStore(pinia);
    return Boolean(caseStore.id) || state.showSigningByEmail.value;
  });

  function setContext() {
    const _showSigningByEmail = state.showSigningByEmail.value;
    const _showCompletedContext = state.showCompletedContext.value;
    $reset();
    state.showSigningByEmail.value = _showSigningByEmail;
    state.showCompletedContext.value = _showCompletedContext;

    const caseStore = useCaseStore(pinia);
    if (caseStore.id) useCaseContext();
    else useEappContext();
  }

  function useCaseContext() {
    const instance = useInstanceSettingsStore(pinia);
    const caseStore = useCaseStore(pinia);
    state.caseId.value = caseStore.id;
    state.caseUuid.value = caseStore.uuid;
    state.status.value = caseStore.status;
    state.premium.value = caseStore.premium;
    state.mode.value = caseStore.mode;
    state.policyNumber.value = caseStore.policy_number;
    state.state.value = caseStore.state;
    state.appliedRating.value = caseStore.applied_rating;
    state.approvedRating.value = caseStore.approved_rating;
    state.appliedPremium.value = caseStore.applied_premium;
    state.approvedPremium.value = caseStore.approved_premium;
    state.line.value = caseStore.line;

    let _amount = caseStore.face_amount;
    if (state.line.value === "ltc") _amount = caseStore.pool_of_money;
    state.amount.value = _amount;

    state.product.value.name = caseStore.product.name;

    state.carrier.value.avatarUrl = caseStore.carrier.avatar_url;
    state.carrier.value.name = caseStore.carrier.name;

    state.primaryAgent.value.name = caseStore.primary_agent.name;
    state.primaryAgent.value.phone = caseStore.primary_agent.phone_work;
    state.primaryAgent.value.email = caseStore.primary_agent.email;
    state.primaryAgent.value.type = caseStore.primary_agent.type;
    state.primaryAgent.value.avatarUrl = instance.logo;
    if (caseStore.primary_agent.scheduling_link) {
      const link = caseStore.primary_agent.scheduling_link
        .replace("https://", "")
        .replace("http://", "");
      state.primaryAgent.value.schedulingLink = `https://${link}`;
    }

    state.inforceDate.value = caseStore.inforce_date;

    state.requirements.value.push(...caseStore.requirements);

    caseStore.contract_parties.forEach((party: CaseContractParty) => {
      state.contractParties.value.push({
        name: party.name,
        roles: party.roles.map((r: CaseContractPartyRole) => ({
          role: r.role,
          beneficiaryAmount: r.beneficiary_amount,
        })),
      });
    });

    if (caseStore.sharable_apply_link) {
      try {
        const url = new URL(caseStore.sharable_apply_link);
        url.searchParams.append("prefill", "true");
        url.searchParams.append(
          "metadata",
          JSON.stringify({
            eapp_id: caseStore.eapp_id,
            case_id: caseStore.id,
          })
        );

        state.sharableLink.value = url.toString();
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        // do nothing
      }
    }
  }

  function useEappContext() {
    const eApp = useEappStore(pinia);
    const quoting = useQuotingStore(pinia);
    const insured = usePrimaryInsuredStore(pinia);
    const instance = useInstanceSettingsStore(pinia);
    state.status.value = "Pending";
    state.state.value = quoting.params.state || null;

    state.primaryAgent.value.type = instance.domainContacts[0].subtitle;
    state.primaryAgent.value.name = instance.domainContacts[0].title;
    state.primaryAgent.value.email = instance.domainContacts[0].email;
    state.primaryAgent.value.phone = instance.phone;
    state.primaryAgent.value.avatarUrl = instance.logo;
    if (instance.scheduling_link) {
      const link = instance.scheduling_link
        .replace("https://", "")
        .replace("http://", "");
      state.primaryAgent.value.schedulingLink = `https://${link}`;
    }

    const isNumber = (v: any) => typeof v === "number";
    const isString = (v: any) => typeof v === "string";
    if (isNumber(eApp.selectedQuote.premium)) {
      state.premium.value = eApp.selectedQuote.premium;
    }
    if (isNumber(eApp.selectedQuote.mode)) {
      state.mode.value = eApp.selectedQuote.mode;
    }
    if (isString(eApp.selectedQuote?.carrier?.avatar_url)) {
      state.carrier.value.avatarUrl = eApp.selectedQuote.carrier.avatar_url;
    }
    if (isString(eApp.selectedQuote?.carrier?.name)) {
      state.carrier.value.name = eApp.selectedQuote.carrier.name;
    }

    state.product.value.name = eApp.selectedQuote.product.name;
    const categories = eApp.selectedQuoteCategories || [];

    let _line = "life";
    let _amount =
      eApp.selectedQuote.initial_death_benefit ||
      eApp.selectedQuote.face_amount;
    if (categories.includes(PRODUCT_TYPE.LTC)) {
      _line = "ltc";
      _amount = eApp.selectedQuote.pool_of_money;
    }

    state.line.value = _line;
    if (isNumber(_amount)) state.amount.value = _amount;

    state.resumeCarrierEapp.value = eApp.resume_carrier_eapp;
    if (eApp.limit_carrier_eapp_to_insured) {
      state.resumeCarrierEappText.value = `${insured.name} must finish this application on ${state.carrier.value.name}'s website`;
    } else if (eApp.resume_carrier_eapp) {
      state.resumeCarrierEappText.value = `${state.carrier.value.name}'s Application requires additional information`;
    }

    state.requirements.value = eApp.requirements;

    if (eApp.sharable_apply_link) {
      try {
        const url = new URL(eApp.sharable_apply_link);

        url.searchParams.append("prefill", "true");
        url.searchParams.append(
          "metadata",
          JSON.stringify({ eapp_id: eApp.id })
        );

        state.sharableLink.value = url.toString();
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        // do nothing
      }
    }

    const roleMapper = (v: StoredContractParty) => {
      const roles: ContractPartyRole[] = [];
      Object.keys(v.roles).forEach((role: RoleType) => {
        if (!v.roles[role]) return;
        if (role.indexOf("Beneficiary") === -1) roles.push({ role });
        else
          roles.push({
            role,
            beneficiaryAmount: integerToPercent(v.beneficiary_amount.model),
          });
      });
      return roles;
    };

    state.contractParties.value.push({
      name: insured.displayName,
      roles: roleMapper(insured as StoredContractParty),
    });

    const jointInsured = useJointInsuredStore(pinia);
    if (jointInsured.id) {
      state.contractParties.value.push({
        name: jointInsured.displayName,
        roles: roleMapper(jointInsured as StoredContractParty),
      });
    }

    eApp.parties.forEach((partyKey: string) => {
      const party = getPartyStore(partyKey, pinia);
      state.contractParties.value.push({
        name: party.displayName,
        roles: roleMapper(party),
      });
    });
  }

  return {
    ...state,

    step,
    next,
    previous,
    hidesFooter,
    hidesHeader,
    hidesShare,
    skipStepSave,

    $reset,
    setContext,
    useCaseContext,
    useEappContext,
  };
}

export const useStepCompletedStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, CompletedStep>("step-completed", () => {
    return initializeCompletedStep(pinia);
  })(pinia, hot);
};
